import { type B2bConfig, type B2bCustomer, type B2bScheme, closeB2bScheme, deleteB2bScheme, listB2bSchemes, publishB2bScheme } from '@api/b2b';
import { DataSheet, type DataSheetColumn } from '@components/ui/DataSheet';
import { Button, Checkbox } from '@mantine/core';
import { PlusIcon } from '@phosphor-icons/react';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import styles from './b2b.module.css';
import SchemeEditor from './SchemeEditor';
import { blankDraft, editingFor, type SchemeEditing } from './schemeDrafts';
import { Pill } from './shared';
import { describeScheme, errorMessage, formatDay, todayIst } from './utils';


const targetOf = (s: B2bScheme, customers: B2bCustomer[]): string => {
    if (s.appliesToAll) return 'All customers';
    const parts = [
        ...s.segments.map((k) => `Segment ${k}`),
        ...s.tags.map((t) => `Tag ${t}`),
        ...s.customerIds.map((id) => customers.find((c) => c.customerId === id)?.name ?? id),
    ];
    return parts.join(', ') || '—';
};

/**
 * Schemes: one row per version. A draft is edited freely; publishing makes it the live version of
 * its code (the previous live version is superseded and ends the day before). Orders keep a
 * snapshot of what they earned, so editing a scheme never changes an order already placed.
 */
const SchemesSection: React.FC<{ config: B2bConfig | undefined; customers: B2bCustomer[]; isAdmin: boolean }> = ({ config, customers, isAdmin }) => {
    const queryClient = useQueryClient();
    const [includeEnded, setIncludeEnded] = useState(false);
    const { data: schemes = [], isLoading } = useQuery({ queryKey: ['b2b-schemes', includeEnded], queryFn: () => listB2bSchemes(includeEnded) });
    const [editing, setEditing] = useState<SchemeEditing | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const refresh = () => queryClient.invalidateQueries({ queryKey: ['b2b-schemes'] });

    const startEdit = async (scheme: B2bScheme) => {
        setBusy(true);
        setError(null);
        try {
            setEditing(await editingFor(scheme, schemes));
            await refresh();
        } catch (e) {
            setError(errorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    const run = async (action: () => Promise<unknown>) => {
        setBusy(true);
        setError(null);
        try {
            await action();
            await refresh();
            return true;
        } catch (e) {
            setError(errorMessage(e));
            return false;
        } finally {
            setBusy(false);
        }
    };

    const columns: DataSheetColumn<B2bScheme>[] = [
        { key: 'name', header: 'Scheme', width: 220, sticky: true, render: (v: string, r) => <span>{v} <span className={styles.muted}>{r.code} v{r.versionNo}</span></span> },
        { key: 'status', header: 'Status', width: 110, render: (v: string) => <Pill label={v} /> },
        { key: 'rule', header: 'Benefit', width: 300, render: (_v, r) => describeScheme(r) },
        { key: 'target', header: 'For', width: 200, render: (_v, r) => targetOf(r, customers) },
        { key: 'effectiveFrom', header: 'From', width: 110, render: (v: string) => formatDay(v) },
        { key: 'effectiveTo', header: 'To', width: 110, render: (v: string | null) => (v ? formatDay(v) : 'Open') },
        {
            key: 'actions',
            header: '',
            width: 260,
            render: (_v, r) => (
                <div className={styles.row}>
                    {r.status === 'DRAFT' ? (
                        <>
                            <Button size="xs" variant={ButtonVariant.SUBTLE} onClick={() => void startEdit(r)}>
                                Edit
                            </Button>
                            <Button size="xs" variant={ButtonVariant.ACTION} disabled={!isAdmin} loading={busy} onClick={() => void run(() => publishB2bScheme(r.id))}>
                                Publish
                            </Button>
                            <Button size="xs" variant={ButtonVariant.SUBTLE} onClick={() => void run(() => deleteB2bScheme(r.id))}>
                                Delete
                            </Button>
                        </>
                    ) : null}
                    {r.status === 'ACTIVE' ? (
                        <>
                            <Button size="xs" variant={ButtonVariant.SUBTLE} loading={busy} onClick={() => void startEdit(r)}>
                                Edit
                            </Button>
                            <Button size="xs" variant={ButtonVariant.SUBTLE} disabled={!isAdmin} onClick={() => void run(() => closeB2bScheme(r.id, todayIst()))}>
                                End today
                            </Button>
                        </>
                    ) : null}
                </div>
            ),
        },
    ];

    return (
        <div className={styles.stack}>
            <DataSheet<B2bScheme>
                title="Schemes"
                subtitle="Free goods and discounts applied to orders automatically, by slab"
                data={schemes}
                columns={columns}
                getRowKey={(r) => r.id}
                isLoading={isLoading}
                maxHeight="auto"
                emptyState="No schemes yet."
                toolbarControls={
                    <div className={styles.row}>
                        <Checkbox size="xs" label="Show ended" checked={includeEnded} onChange={(e) => setIncludeEnded(e.currentTarget.checked)} />
                        <Button size="xs" variant={ButtonVariant.ACTION} leftSection={<PlusIcon size={14} />} onClick={() => setEditing({ id: null, draft: blankDraft() })}>
                            New scheme
                        </Button>
                    </div>
                }
            />
            {error ? <span className={styles.error}>{error}</span> : null}
            {editing ? <SchemeEditor editing={editing} config={config} customers={customers} isAdmin={isAdmin} onClose={() => setEditing(null)} /> : null}
        </div>
    );
};

export default SchemesSection;
